import React, { useState, useRef, useEffect } from 'react';
import Button from '../common/Button';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// More menu icon for mobile
const MoreIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
  </svg>
);

// Dropdown menu for mobile actions
function ActionMenu({ user, onEdit, onResign, onReactivate, onDelete, currentUserId, isSystemAdmin }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const canResign = !user.isResigned && user.id !== currentUserId;
  const canReactivate = user.isResigned && user.id !== currentUserId;
  const canDelete = isSystemAdmin && user.id !== currentUserId;

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
        aria-label="More actions"
      >
        <MoreIcon />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-30 overflow-hidden">
          <button
            onClick={() => {
              onEdit(user);
              setIsOpen(false);
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          {canResign && (
            <button
              onClick={() => {
                onResign(user);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Resign
            </button>
          )}
          {canReactivate && (
            <button
              onClick={() => {
                onReactivate(user);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reactivate
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => {
                onDelete(user);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Expandable row for mobile details
function ExpandableDetails({ user, getReportsTo }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-xs text-tenq-gold hover:text-tenq-gold-dark flex items-center gap-1"
      >
        {isExpanded ? 'Hide details' : 'Show details'}
        <svg
          className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="mt-2 pt-2 border-t border-gray-100 text-xs space-y-1">
          <div>
            <span className="text-gray-400">Email: </span>
            <span className="text-gray-600">{user.email}</span>
          </div>
          <div>
            <span className="text-gray-400">Start Date: </span>
            <span className="text-gray-600">
              {user.startDate ? new Date(user.startDate).toLocaleDateString() : '-'}
            </span>
          </div>
          <div>
            <span className="text-gray-400">Reports To: </span>
            <span className="text-gray-600">{getReportsTo(user)}</span>
          </div>
        </div>
      )}
    </>
  );
}

function UserTable({ users, onEdit, onResign, onReactivate, onDelete, currentUserId, isSystemAdmin }) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const getRoleBadge = (role) => {
    const badges = {
      system_admin: 'badge bg-gray-900 text-white',
      admin: 'badge bg-rose-100 text-rose-800',
      director: 'badge bg-red-100 text-red-800',
      manager: 'badge bg-purple-100 text-purple-800',
      leader: 'badge bg-blue-100 text-blue-800',
      agent: 'badge bg-green-100 text-green-800',
      telemarketer: 'badge bg-orange-100 text-orange-800',
      sid: 'badge bg-yellow-100 text-yellow-800',
      asid: 'badge bg-amber-100 text-amber-800',
      designer: 'badge bg-pink-100 text-pink-800',
      draughter_2d: 'badge bg-indigo-100 text-indigo-800',
      draughter_site: 'badge bg-violet-100 text-violet-800',
      quotation_team: 'badge bg-teal-100 text-teal-800',
      legal_team: 'badge bg-slate-100 text-slate-800',
      site_supervisor: 'badge bg-lime-100 text-lime-800',
      site_manager: 'badge bg-emerald-100 text-emerald-800',
      operation_head: 'badge bg-cyan-100 text-cyan-800'
    };
    return badges[role] || 'badge bg-gray-100 text-gray-800';
  };

  const getRoleDisplayName = (role) => {
    const names = {
      system_admin: 'System Admin',
      admin: 'Admin',
      director: 'Director',
      manager: 'Manager',
      leader: 'Leader',
      agent: 'Agent',
      telemarketer: 'Telemarketer',
      sid: 'SID',
      asid: 'ASID',
      designer: 'Designer (3D)',
      draughter_2d: '2D Draughter',
      draughter_site: 'Site Draughter',
      quotation_team: 'Quotation Team',
      legal_team: 'Legal Team',
      site_supervisor: 'Site Supervisor',
      site_manager: 'Site Manager',
      operation_head: 'Operation Head'
    };
    return names[role] || role;
  };

  const getReportsTo = (user) => {
    // Leaders report to their manager
    if (user.role === 'leader' && user.manager) {
      return <span className="text-purple-600">{user.manager.nickname} (Manager)</span>;
    }
    // Agents and telemarketers report to their leader
    if ((user.role === 'agent' || user.role === 'telemarketer') && user.leader) {
      return <span className="text-blue-600">{user.leader.nickname} (Leader)</span>;
    }
    // Design team members report to their SID
    if (['asid', 'designer', 'draughter_2d'].includes(user.role) && user.sid) {
      return <span className="text-tenq-gold">{user.sid.nickname} (SID)</span>;
    }
    // Site supervisors report to their site manager
    if (user.role === 'site_supervisor' && user.siteManager) {
      return <span className="text-emerald-600">{user.siteManager.nickname} (Site Manager)</span>;
    }
    return '-';
  };


  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
        <p className="mt-1 text-sm text-gray-500">Get started by creating a new user.</p>
      </div>
    );
  }

  // Mobile: Card-style list layout
  if (isMobile) {
    return (
      <div className="divide-y divide-gray-200">
        {users.map((user) => (
          <div key={user.id} className="py-3 px-2 hover:bg-gray-50 active:bg-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex items-start flex-1 min-w-0">
                {/* Avatar */}
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-gold flex items-center justify-center mr-3">
                  <span className="text-white font-medium text-sm">
                    {user.nickname ? user.nickname.substring(0, 2).toUpperCase() : (user.fullName ? user.fullName.split(' ').map(n => n[0]).join('').toUpperCase() : '?')}
                  </span>
                </div>

                {/* User Info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 text-sm">{user.nickname || user.fullName || 'N/A'}</span>
                    <span className={getRoleBadge(user.role)}>
                      {getRoleDisplayName(user.role)}
                    </span>
                    {user.isResigned && (
                      <span className="badge bg-gray-200 text-gray-600">Resigned</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{user.fullName}</div>

                  {/* Expandable details */}
                  <div className="mt-2">
                    <ExpandableDetails user={user} getReportsTo={getReportsTo} />
                  </div>
                </div>
              </div>

              {/* Action Menu */}
              <ActionMenu
                user={user}
                onEdit={onEdit}
                onResign={onResign}
                onReactivate={onReactivate}
                onDelete={onDelete}
                currentUserId={currentUserId}
                isSystemAdmin={isSystemAdmin}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Desktop: Full table layout with sticky header
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-tenq-gray-light sticky top-0 z-10">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Name / Nickname
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Start Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Reports To
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-tenq-gray-light transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-gradient-gold flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {user.nickname ? user.nickname.substring(0, 2).toUpperCase() : (user.fullName ? user.fullName.split(' ').map(n => n[0]).join('').toUpperCase() : '?')}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{user.nickname || user.fullName || 'N/A'}</div>
                    <div className="text-xs text-gray-500">{user.fullName}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{user.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={getRoleBadge(user.role)}>
                  {getRoleDisplayName(user.role)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {user.startDate ? new Date(user.startDate).toLocaleDateString() : '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {getReportsTo(user)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline-gold"
                    size="sm"
                    onClick={() => onEdit(user)}
                  >
                    Edit
                  </Button>
                  {!user.isResigned && user.id !== currentUserId && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => onResign(user)}
                    >
                      Resign
                    </Button>
                  )}
                  {user.isResigned && user.id !== currentUserId && (
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => onReactivate(user)}
                    >
                      Reactivate
                    </Button>
                  )}
                  {isSystemAdmin && user.id !== currentUserId && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDelete(user)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UserTable;
